import React, { useContext, useEffect, useState } from "react";
import logo from "assets/img/logos/barlleno-logo-blanco.png";
import { useNavigate } from 'react-router-dom';
import UserContextProvider from "interface/interfaces";
import { sessionService } from "service/sessionService";
import { useLocalStorage } from "hooks/useLocalStorage";
import menuIcon from "assets/icons/menu.svg";
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LogoutIcon from '@mui/icons-material/Logout';

const NavBar = ({ showButtons }: any) => {
  const [sessionLog, setSessionLog] = useState(false);
  const context = useContext(UserContextProvider);
  const navigate = useNavigate();
  const [isActive, setActive] = useState(false);
  const { removeItem } = useLocalStorage();

  // 1) Efecto inicial
  useEffect(() => {
    const userCurrent = sessionService.getCurrentUser();
    if (userCurrent != null) {
      setSessionLog(true);
      context.asignateUser(userCurrent);
    } else {
      setSessionLog(false);
    }
  }, []);

  // 2) Reacciona cuando cambia el contexto
  useEffect(() => {
    const hasCtx = !!context?.user && !!(context.user.id || context.user.uid);
    setSessionLog(hasCtx);
  }, [context?.user]);

  // 3) Reacciona a eventos de sesión
  useEffect(() => {
    const handleAuthChanged = () => {
      const u = sessionService.getCurrentUser();
      setSessionLog(!!u && !!(u.id || u.uid));
      try { context.asignateUser?.(u); } catch { }
    };

    window.addEventListener("storage", handleAuthChanged);
    window.addEventListener("auth:changed", handleAuthChanged);
    return () => {
      window.removeEventListener("storage", handleAuthChanged);
      window.removeEventListener("auth:changed", handleAuthChanged);
    };
  }, [context]);

  const logout = () => {
    setSessionLog(false);
    sessionService.destroySession();
    removeItem("user");
    try { context.asignateUser(null as any); } catch { }
    navigate("/");
  };

  const redirect = (option: string) => {
    option === 'login' ? navigate("/login") : navigate("/register/");
  };

const goToSection = (sectionId: string) => {
  const onHome = window.location.pathname === '/home';

  if (onHome) {
    const el = document.getElementById(sectionId) || document.querySelector(`[data-anchor="${sectionId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    // hash + state para máxima compatibilidad
    navigate(
      { pathname: '/home', hash: `#${sectionId}` },
      { state: { anchor: sectionId }, replace: false }
    );
  }
};


  return (
    <>
      <div className="absolute z-[21] w-full">
        {/* Desktop navbar */}
        <div className="hidden md:block p-14 ">
          <div className="mx-auto lg:mx-[5rem] px-6">
            {/* 3 columnas: [logo] [links centrados] [acciones derecha] */}
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6 h-20">
              {/* LOGO (izquierda) */}
              <div className="min-w-[15rem] flex items-center">
                <img
                  src={logo}
                  alt="Logo barllenotickets"
                  className="w-[15rem] cursor-pointer"
                  onClick={() => navigate("/")}
                />
              </div>

              {/* LINKS (centro) — se mantienen centrados y sin saltos */}
              <nav
                className="flex items-center justify-center font-semibold text-white gap-8 select-none"
              >
                {/* Tip: border transparente para evitar salto en hover */}
                <button
                  onClick={() => goToSection('inicio')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Inicio
                </button>

                {sessionLog && (
                  <button
                    onClick={() => navigate('/my-tickets')}
                    className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                               border-b-2 border-transparent hover:border-primary"
                  >
                    Mis Tickets
                  </button>
                )}

                <button
                  onClick={() => goToSection('proxevents')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Próximos Eventos
                </button>

                <button
                  onClick={() => goToSection('review')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Reviews
                </button>

                {/* si agregas más links, clona el patrón; el centro siempre queda centrado */}
              </nav>

              {/* ACCIONES (derecha) — ancho fijo para que no mueva nada */}
              <div className="w-[340px] flex items-center justify-end gap-3">
                {sessionLog ? (
                  <div className="relative group">
                    <button className="flex items-center gap-2 h-12 px-4 rounded-full bg-graffito/80 border border-primary/50 text-white font-semibold transition hover:bg-graffito hover:border-primary">
                      {context.user?.image ? (
                        <div className="w-8 h-8 rounded-full bg-cover bg-center border border-primary" style={{ backgroundImage: `url(${context.user.image})` }} />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                          {context.user?.name ? context.user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                      <span className="max-w-[100px] truncate">{context.user?.name || 'Usuario'}</span>
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-56 bg-graffito border border-gray-700 rounded-xl shadow-2xl py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right scale-95 group-hover:scale-100">
                      <button onClick={() => navigate('/profile')} className="flex items-center gap-3 w-full text-left px-5 py-3 text-sm text-gray-300 font-medium hover:bg-black/40 hover:text-primary transition-colors">
                        <PersonIcon fontSize="small" /> Mi Perfil
                      </button>
                      <button onClick={() => navigate('/my-tickets')} className="flex items-center gap-3 w-full text-left px-5 py-3 text-sm text-gray-300 font-medium hover:bg-black/40 hover:text-primary transition-colors">
                        <ConfirmationNumberIcon fontSize="small" /> Mis Tickets
                      </button>
                      <div className="border-t border-gray-700 my-2"></div>
                      <button onClick={logout} className="flex items-center gap-3 w-full text-left px-5 py-3 text-sm text-red-500 font-medium hover:bg-black/40 hover:text-red-400 transition-colors">
                        <LogoutIcon fontSize="small" /> Cerrar sesión
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => redirect('login')}
                      className="h-10 px-5 rounded-full border-2 border-primary bg-[#2c2b30] text-white font-bold transition hover:bg-primary/20 flex items-center justify-center whitespace-nowrap"
                    >
                      Iniciar Sesión
                    </button>
                    <button
                      onClick={() => redirect('register')}
                      className="h-10 px-5 rounded-full bg-primary text-white font-bold shadow-[0_0_15px_rgba(232,65,55,0.5)] transition hover:bg-primary/90 uppercase flex items-center justify-center whitespace-nowrap"
                    >
                      REGISTRARSE
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="w-full justify-between flex md:hidden relative p-7">
          <div>
            <img
              src={logo}
              alt="Logo barllenotickets"
              className="w-36 cursor-pointer"
              onClick={() => navigate("/home")}
            />
          </div>
          <img onClick={() => setActive(!isActive)} src={menuIcon} className="w-12" alt="" />
        </div>

        <div className={`${isActive ? "flex w-full fixed h-screen top-0 z-50" : "hidden"}`}>
          <div
            className="w-[100%] bg-black bg-opacity-70 hover:cursor-pointer"
            onClick={() => setActive(!isActive)}
          />
          <div className="w-[35rem] h-full bg-graffito flex flex-col relative">
            <ClearIcon style={{ fontSize: "32px" }} className="m-3 text-primary" onClick={() => setActive(!isActive)} />
            
            {sessionLog && (
               <div className="flex flex-col items-center justify-center mt-4">
                  {context.user?.image ? (
                     <div className="w-20 h-20 rounded-full bg-cover bg-center border-2 border-primary" style={{ backgroundImage: `url(${context.user.image})` }} />
                  ) : (
                     <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-bold">
                        {context.user?.name ? context.user.name.charAt(0).toUpperCase() : 'U'}
                     </div>
                  )}
                  <p className="text-white text-xl font-bold mt-2">{context.user?.name} {context.user?.lastname}</p>
                  <p className="text-gray-400 text-sm">{context.user?.email}</p>
               </div>
            )}

            <div className={`w-full flex flex-col font-Montserrat font-bold text-xl items-center justify-center text-center text-white gap-8 px-5 ${sessionLog ? 'mt-4' : 'mt-8 h-[60%]'}`}>
              {/* Puedes usar secciones del Home también en mobile */}
              <button onClick={() => { setActive(false); goToSection('inicio'); }}>Home</button>
              <button onClick={() => { setActive(false); goToSection('proxevents'); }}>Próximos Eventos</button>
              <button onClick={() => { setActive(false); goToSection('review'); }}>Reviews</button>

              {sessionLog && <button onClick={() => { setActive(false); navigate("/profile"); }}>Mi Perfil</button>}
              {sessionLog && <button onClick={() => { setActive(false); navigate("/my-tickets"); }}>Mis Tickets</button>}

              {sessionLog && (
                <button
                  onClick={() => { setActive(false); logout(); }}
                  className="hover:-translate-y-1 hover:scale-110 hover:text-primary hover:border-b-2 border-primary"
                >
                  Cerrar sesión
                </button>
              )}

              {!sessionLog && showButtons && (
                <div className="flex items-center justify-between flex-col">
                  <div className="flex flex-col w-full items-center h-full gap-5 mt-4">
                    <button 
                      className="h-12 w-[80%] rounded-full border-2 border-primary bg-[#2c2b30] text-white font-bold transition hover:bg-primary/20 flex items-center justify-center" 
                      onClick={() => { setActive(false); navigate("/login"); }}
                    >
                      Iniciar Sesión
                    </button>
                    <button 
                      className="h-12 w-[80%] rounded-full bg-primary text-white font-bold shadow-[0_0_15px_rgba(232,65,55,0.5)] transition hover:bg-primary/90 uppercase flex items-center justify-center" 
                      onClick={() => { setActive(false); navigate("/register"); }}
                    >
                      REGISTRARSE
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NavBar;
